import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * ScrollToTop
 * ---------------------------------------------------------------
 * Client-side route change scroll manager for Suntronix ONE.
 *
 * Why this exists:
 *   React Router v6 does NOT restore scroll position by default,
 *   so navigating from a long page (e.g. /assets) to another page
 *   (/reports) keeps the previous vertical offset — new page opens
 *   mid-way, sometimes on blank space until the user scrolls up.
 *
 * Strategy:
 *   1. Reset the WINDOW scroll (the AppShell relies on window
 *      scrolling because it uses `min-h-screen flex` with no
 *      overflow clipping on the outer or `main` container).
 *   2. Also reset the primary `<main>` scroll container and the
 *      `#main-content` element, in case any page introduces a
 *      nested `overflow-y-auto` shell (defensive, cheap).
 *   3. Reset any element flagged with `data-scroll-container="true"`
 *      so page authors can opt-in future custom containers.
 *   4. Use `instant` behavior — a smooth animation on route change
 *      would only *worsen* the perceived jank.
 *
 * The effect runs on pathname change (search params changing on
 * the same page should NOT scroll to top — e.g. sort/filter
 * updates on /assets).
 */
export default function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // Defer to next paint so layout has stabilised (fonts, images).
    const raf = requestAnimationFrame(() => {
      try {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      } catch {
        // Older browsers reject the options-object form.
        window.scrollTo(0, 0);
      }
      // Also touch documentElement + body in case CSS makes one of
      // them the scrolling element instead of the window.
      if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;

      // Main content region (see AppShell's <div id="main-content">)
      const main = document.getElementById("main-content");
      if (main) main.scrollTop = 0;

      // The outer <main> element AppShell renders (flex-col column)
      document.querySelectorAll("main").forEach((el) => { el.scrollTop = 0; });

      // Any opt-in custom scroll container
      document.querySelectorAll("[data-scroll-container='true']").forEach((el) => {
        el.scrollTop = 0;
      });
    });

    return () => cancelAnimationFrame(raf);
  }, [pathname]);

  return null;
}
